import { Routine } from 'app/modules/admin/models/routines.types';
import { User } from 'app/modules/admin/models/users.types';
import { Device } from 'app/modules/admin/models/devices.types';
import { RoutineStatusColor } from '../types/routine-detonator.types';

/**
 * Utilidades para el componente RoutineDetonator
 * Funciones puras sin efectos secundarios
 */

/**
 * Obtiene las iniciales de un jugador
 */
export function getPlayerInitials(player: User | null): string {
    if (!player?.name) return '';

    const names = player.name.trim().split(' ');
    if (names.length >= 2) {
        return (names[0][0] + names[names.length - 1][0]).toUpperCase();
    } else {
        return names[0].substring(0, 2).toUpperCase();
    }
}

/**
 * Obtiene el alias de un dispositivo
 */
export function getDeviceAlias(device: Device | undefined): string {
    if (!device) return 'Sin alias';

    if (Array.isArray(device.alias)) {
        return device.alias[0]?.alias ?? device.serial ?? 'Sin alias';
    }
    return device.alias || device.serial || 'Sin alias';
}

/**
 * Determina el color de estado de una rutina
 */
export function getRoutineStatusColor(routine: Routine): RoutineStatusColor {
    if (!routine.selected_user_id) {
        return 'default';
    }

    if (routine.cluster?.type_devices?.length > 0) {
        const allDevicesAssigned = routine.cluster.type_devices.every(td => td.selected_device_id);
        return allDevicesAssigned ? 'green' : 'yellow';
    }

    return 'default';
}

/**
 * Obtiene las clases de borde según el estado de la rutina
 */
export function getRoutineBorderClass(routine: Routine): string {
    const color = getRoutineStatusColor(routine);
    switch (color) {
        case 'green':
            return 'border-2 border-green-400';
        case 'yellow':
            return 'border-2 border-yellow-400';
        default:
            return 'border-b-2 border-gray-400';
    }
}

/**
 * Obtiene el tipo de rutina como texto
 */
export function getRoutineType(routine_type_id: number): string {
    const routineTypes: Record<number, string> = {
        1: 'Semáforo',
        2: 'Color Objetivo'
    };
    return routineTypes[routine_type_id] || 'Desconocido';
}

/**
 * Invierte la posición row-col a col-row
 */
export function reversePosition(position: string): string {
    if (!position) return '';
    const [x, y] = position.split('-');
    return `${y}-${x}`;
}

/**
 * Cuenta el número de dispositivos en table_data
 */
export function getDeviceCount(tableData: any): number {
    if (!tableData) return 0;
    return Object.keys(tableData).length;
}

/**
 * Genera un ID único para la ejecución
 */
export function generateExecutionId(routineId: number, userId: number): string {
    return `${routineId}_${userId}_${Date.now()}_${Math.random().toString(36).substring(2, 11)}`;
}

/**
 * Crea un mapeo de posiciones a alias de dispositivos
 */
export function createDeviceAliasMap(routine: Routine, devices: Device[]): { [key: string]: string } {
    const map: { [key: string]: string } = {};

    if (!routine?.cluster?.type_devices) return map;

    for (const td of routine.cluster.type_devices) {
        if (td.selected_device_id && td.pivot?.position) {
            const device = devices.find(d => d.id === td.selected_device_id);
            map[td.pivot.position] = getDeviceAlias(device);
        }
    }

    return map;
}

/**
 * Verifica si una rutina está lista para detonarse
 */
export function isRoutineReady(routine: Routine): boolean {
    if (!routine.selected_user_id) return false;
    if (routine.routine_log_id) return false; // Ya ejecutada

    const allDevicesAssigned = routine.cluster?.type_devices?.every(td => !!td.selected_device_id);
    return !!allDevicesAssigned;
}

/**
 * Filtra las rutinas que están listas para detonarse
 */
export function getReadyRoutines(routines: Routine[]): Routine[] {
    return routines.filter(isRoutineReady);
}

/**
 * Obtiene el nombre de un jugador por su ID
 */
export function getPlayerNameById(players: User[], id: number | null): string {
    if (!id) return '';
    const player = players.find(p => p.id === id);
    return player?.name ?? '';
}

/**
 * Verifica si un jugador ya está seleccionado en alguna rutina
 */
export function isPlayerAlreadySelected(uuid: string, selectedRoutines: Routine[], players: User[]): boolean {
    const selectedUuids = selectedRoutines
        .map(r => players.find(p => p.id === r.selected_user_id)?.uuid)
        .filter(Boolean);
    return selectedUuids.includes(uuid);
}

/**
 * Obtiene los jugadores disponibles para una rutina específica
 */
export function getAvailablePlayers(
    routine: Routine | undefined,
    allPlayers: User[],
    selectedRoutines: Routine[],
    currentUserId?: number
): User[] {
    const divisionIds = routine?.divisions?.map(d => d.id) ?? [];
    const selectedUserIds = selectedRoutines
        .map(r => r.selected_user_id)
        .filter(id => id !== null && id !== currentUserId);

    return allPlayers.filter(p =>
        !selectedUserIds.includes(p.id) && divisionIds.includes(p.division_id)
    );
}

/**
 * Extrae el mensaje de error de un objeto de error
 */
export function extractErrorMessage(error: any): string {
    return error?.message || error?.error || JSON.stringify(error) || 'Error desconocido';
}
